const DISTANCE_WARNING_THRESHOLD_MILES = 50;
const EARTH_RADIUS_MILES = 3958.7613;
const ACKNOWLEDGEMENT_KEY_PREFIX = "distant-location-warning-acknowledged:";
const EXCLUDED_HOSTNAMES = [
    "onboarding.arrowpos.com",
    "recurve.arrowpos.com",
    "orders.arrowpos.com",
];

const toCoordinate = (value, minimum, maximum) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    const coordinate = Number(value);
    if (!Number.isFinite(coordinate) || coordinate < minimum || coordinate > maximum) {
        return null;
    }

    return coordinate;
};

const normalizeCoordinates = (value) => {
    if (!value) {
        return null;
    }

    const latitude = toCoordinate(value.latitude, -90, 90);
    const longitude = toCoordinate(value.longitude, -180, 180);

    if (latitude === null || longitude === null) {
        return null;
    }

    return { latitude, longitude };
};

const calculateHaversineDistanceMiles = (origin, destination) => {
    const degreesToRadians = (degrees) => degrees * (Math.PI / 180);
    const originLatitude = degreesToRadians(origin.latitude);
    const destinationLatitude = degreesToRadians(destination.latitude);
    const latitudeDifference = destinationLatitude - originLatitude;
    const longitudeDifference = degreesToRadians(destination.longitude - origin.longitude);

    const haversine = Math.sin(latitudeDifference / 2) ** 2
        + Math.cos(originLatitude)
        * Math.cos(destinationLatitude)
        * Math.sin(longitudeDifference / 2) ** 2;
    const angularDistance = 2 * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));

    return EARTH_RADIUS_MILES * angularDistance;
};

const shouldWarnForDistance = (distanceMiles, thresholdMiles = DISTANCE_WARNING_THRESHOLD_MILES) => {
    return distanceMiles > thresholdMiles;
};

const formatApproximateDistanceMiles = (distanceMiles) => Math.round(distanceMiles);

const isExcludedHostname = (hostname) => EXCLUDED_HOSTNAMES.includes(String(hostname || "").toLowerCase());

const acknowledgementKey = (storeId) => `${ACKNOWLEDGEMENT_KEY_PREFIX}${storeId}`;

const isAcknowledged = (sessionStorage, storeId) => {
    if (!sessionStorage || storeId === null || storeId === undefined) {
        return false;
    }

    try {
        return sessionStorage.getItem(acknowledgementKey(storeId)) === "true";
    } catch (error) {
        return false;
    }
};

const acknowledgeStore = (sessionStorage, storeId) => {
    if (!sessionStorage || storeId === null || storeId === undefined) {
        return;
    }

    try {
        sessionStorage.setItem(acknowledgementKey(storeId), "true");
    } catch (error) {
        // Session storage may be unavailable in privacy-focused browser modes.
    }
};

const getBrowserCoordinates = (geolocation) => {
    if (!geolocation || typeof geolocation.getCurrentPosition !== "function") {
        return Promise.resolve(null);
    }

    return new Promise((resolve) => {
        try {
            geolocation.getCurrentPosition(
                (position) => resolve(normalizeCoordinates(position && position.coords)),
                () => resolve(null),
                {
                    enableHighAccuracy: false,
                    maximumAge: 300000,
                    timeout: 5000,
                }
            );
        } catch (error) {
            resolve(null);
        }
    });
};

const evaluateDistantLocationWarning = async ({
    hostname,
    store,
    sessionStorage,
    geolocation,
    fetchIpCoordinates,
    thresholdMiles = DISTANCE_WARNING_THRESHOLD_MILES,
}) => {
    if (isExcludedHostname(hostname) || !store || isAcknowledged(sessionStorage, store.id)) {
        return null;
    }

    const storeCoordinates = normalizeCoordinates(store);
    if (!storeCoordinates) {
        return null;
    }

    let customerCoordinates = await getBrowserCoordinates(geolocation);
    if (!customerCoordinates && typeof fetchIpCoordinates === "function") {
        try {
            customerCoordinates = normalizeCoordinates(await fetchIpCoordinates());
        } catch (error) {
            customerCoordinates = null;
        }
    }

    if (!customerCoordinates) {
        return null;
    }

    const distanceMiles = calculateHaversineDistanceMiles(customerCoordinates, storeCoordinates);
    if (!shouldWarnForDistance(distanceMiles, thresholdMiles)) {
        return null;
    }

    return { distanceMiles };
};

module.exports = {
    DISTANCE_WARNING_THRESHOLD_MILES,
    acknowledgeStore,
    calculateHaversineDistanceMiles,
    evaluateDistantLocationWarning,
    formatApproximateDistanceMiles,
    getBrowserCoordinates,
    isAcknowledged,
    isExcludedHostname,
    normalizeCoordinates,
    shouldWarnForDistance,
};
